import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { dashboardAPI, cranesAPI, sitesAPI } from '@/lib/api';
import { Truck, Activity, DollarSign, TrendingDown, TrendingUp, Users, MapPin, ChevronDown, ChevronUp, BarChart3, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'sonner';
import { CategorySummaryCard } from '@/components/CategorySummaryCard';
import { ExpiringDocsBox } from '@/components/ExpiringDocsBox';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons
const greenIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const orangeIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Indian city coordinates lookup
const cityCoordinates = {
  // Major cities
  'mumbai': [19.0760, 72.8777],
  'delhi': [28.6139, 77.2090],
  'new delhi': [28.6139, 77.2090],
  'bangalore': [12.9716, 77.5946],
  'bengaluru': [12.9716, 77.5946],
  'hyderabad': [17.3850, 78.4867],
  'chennai': [13.0827, 80.2707],
  'kolkata': [22.5726, 88.3639],
  'pune': [18.5204, 73.8567],
  'ahmedabad': [23.0225, 72.5714],
  'jaipur': [26.9124, 75.7873],
  'lucknow': [26.8467, 80.9462],
  'kanpur': [26.4499, 80.3319],
  'nagpur': [21.1458, 79.0882],
  'indore': [22.7196, 75.8577],
  'thane': [19.2183, 72.9781],
  'bhopal': [23.2599, 77.4126],
  'visakhapatnam': [17.6868, 83.2185],
  'patna': [25.5941, 85.1376],
  'vadodara': [22.3072, 73.1812],
  'guwahati': [26.1445, 91.7362],
  'chandigarh': [30.7333, 76.7794],
  'coimbatore': [11.0168, 76.9558],
  'kochi': [9.9312, 76.2673],
  'surat': [21.1702, 72.8311],
  'noida': [28.5355, 77.3910],
  'greater noida': [28.4744, 77.5040],
  'g. noida': [28.4744, 77.5040],
  'ghaziabad': [28.6692, 77.4538],
  'faridabad': [28.4089, 77.3178],
  'gurugram': [28.4595, 77.0266],
  'gurgaon': [28.4595, 77.0266],
  // Gujarat
  'mundra': [22.8394, 69.7219],
  'dholera': [22.2475, 72.1932],
  'gandhidham': [23.0753, 70.1337],
  'rajkot': [22.3039, 70.8022],
  'jamnagar': [22.4707, 70.0577],
  'bhavnagar': [21.7645, 72.1519],
  'patan': [23.8493, 72.1266],
  'gj': [22.2587, 71.1924],
  // Delhi NCR
  'aerocity': [28.5562, 77.0932],
  'aerocity delhi': [28.5562, 77.0932],
  'jewar': [28.1307, 77.5556],
  // Mumbai Region
  'belapur': [19.0235, 73.0393],
  'mumbai belapur': [19.0235, 73.0393],
  'navi mumbai': [19.0330, 73.0297],
  // Bihar
  'bihar': [25.0961, 85.3131],
  'sonpur': [25.7013, 85.1772],
  'dhigwara': [25.6000, 85.2000],
  // Northeast
  'assam': [26.2006, 92.9376],
  // Others
  'rajasthan': [27.0238, 74.2179],
  'gujarat': [22.2587, 71.1924],
  'maharashtra': [19.7515, 75.7139],
  'karnataka': [15.3173, 75.7139],
  'tamil nadu': [11.1271, 78.6569],
  'kerala': [10.8505, 76.2711],
  'west bengal': [22.9868, 87.8550],
  'uttar pradesh': [26.8467, 80.9462],
  'madhya pradesh': [22.9734, 78.6569],
  'andhra pradesh': [15.9129, 79.7400],
  'telangana': [18.1124, 79.0193],
  'odisha': [20.9517, 85.0985],
  'jharkhand': [23.6102, 85.2799],
  'chhattisgarh': [21.2787, 81.8661],
  'punjab': [31.1471, 75.3412],
  'haryana': [29.0588, 76.0856],
};

// Function to get coordinates from location string
const getCoordinates = (location) => {
  if (!location) return null;
  
  const locationLower = location.toLowerCase().trim();
  
  // Direct match
  if (cityCoordinates[locationLower]) {
    return cityCoordinates[locationLower];
  }
  
  // Check if any city name is contained in the location
  for (const [city, coords] of Object.entries(cityCoordinates)) {
    if (locationLower.includes(city) || city.includes(locationLower)) {
      return coords;
    }
  }
  
  return null;
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const VsPrev = ({ cur, prev, higherIsGood, label, testId }) => {
  const diff = (cur || 0) - (prev || 0);
  const pct = prev ? Math.round(Math.abs(diff / prev) * 1000) / 10 : null;
  const good = higherIsGood ? diff > 0 : diff < 0;
  const Icon = diff > 0 ? ArrowUpRight : diff < 0 ? ArrowDownRight : Minus;
  return (
    <span className={`flex flex-wrap items-center gap-0.5 font-bold ${diff === 0 ? 'text-muted-foreground' : good ? 'text-emerald-500' : 'text-rose-500'}`} data-testid={testId}>
      <Icon className="w-3.5 h-3.5" />{pct !== null ? `${pct}%` : diff === 0 ? '0%' : 'NEW'}
      <span className="font-normal text-muted-foreground ml-1">vs {label} (₹{Math.round(prev || 0).toLocaleString()})</span>
    </span>
  );
};

export const StatCard = ({ title, value, icon: Icon, trend, pulse, onClick }) => (
  <Card className={`bg-card border border-border rounded-md hover:border-primary/40 hover:shadow-sm transition-all duration-200 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={`stat-card-${title.toLowerCase().replace(/ /g, '-')}`}>
    <CardHeader className="flex flex-row items-center justify-between pb-1 md:pb-2 p-3 md:p-6">
      <p className="text-[10px] md:text-xs uppercase tracking-widest font-bold text-muted-foreground">{title}</p>
      <div className="flex items-center gap-2">
        {pulse && <span className={`w-2 h-2 rounded-full bg-${pulse}-500 pulse-live`}></span>}
        <Icon className="w-4 h-4 md:w-5 md:h-5 text-primary" />
      </div>
    </CardHeader>
    <CardContent className="p-3 md:p-6 pt-0">
      <div className="text-xl md:text-3xl font-heading font-bold tracking-tight text-card-foreground" data-testid={`stat-${title.toLowerCase().replace(/ /g, '-')}`}>{value}</div>
      {trend && (
        <div className="text-[10px] md:text-xs mt-1 md:mt-2 text-muted-foreground">{trend}</div>
      )}
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [cranes, setCranes] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [collapsedWidgets, setCollapsedWidgets] = useState({});
  const navigate = useNavigate();
  const isAdmin = JSON.parse(localStorage.getItem('user') || '{}').role === 'admin';
  const today = new Date();
  const thisYm = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [ym, setYm] = useState(thisYm);
  const [selYear, selMonth] = ym.split('-').map(Number);
  const prevLabel = MONTH_NAMES[(selMonth + 10) % 12];

  useEffect(() => {
    if (loading) return;
    dashboardAPI.getStats({ month: selMonth, year: selYear }).then(r => setStats(r.data)).catch(() => toast.error('Could not load figures for that month'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ym]);

  const toggleWidget = (key) => {
    setCollapsedWidgets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setFetchError(false);
    setLoading(true);
    try {
      const [statsRes, cranesRes, sitesRes] = await Promise.all([
        dashboardAPI.getStats({ month: selMonth, year: selYear }),
        cranesAPI.getAll(),
        sitesAPI.getAll()
      ]);
      setStats(statsRes.data);
      setCranes(cranesRes.data);
      setSites(sitesRes.data);
      
      // Build map markers - grouped by site
      const siteGroups = {};
      const cranesData = cranesRes.data;
      const sitesData = sitesRes.data;
      
      for (const crane of cranesData) {
        let siteId = null;
        let location = null;
        let siteName = null;
        let isIdle = false;
        
        if (crane.deployed_site_id) {
          const site = sitesData.find(s => s.id === crane.deployed_site_id);
          if (site) {
            siteId = site.id;
            location = site.location;
            siteName = site.name;
          }
        }
        
        if (!siteId && crane.idle_site_id) {
          const idleSite = sitesData.find(s => s.id === crane.idle_site_id);
          if (idleSite) {
            siteId = idleSite.id;
            location = idleSite.location;
            siteName = idleSite.name;
            isIdle = true;
          }
        }
        
        if (!siteId && crane.location) {
          siteId = 'loc_' + crane.location;
          location = crane.location;
          siteName = crane.location;
        }
        
        if (siteId && location) {
          const coords = getCoordinates(location);
          if (coords) {
            if (!siteGroups[siteId]) {
              siteGroups[siteId] = {
                siteId,
                siteName,
                location,
                coordinates: coords,
                cranes: [],
                siteStatus: isIdle ? 'idle' : 'active'
              };
            }
            siteGroups[siteId].cranes.push({
              serial_number: crane.serial_number,
              equipment: crane.equipment,
              status: crane.status,
              capacity: crane.capacity
            });
          }
        }
      }
      
      setMapMarkers(Object.values(siteGroups));
    } catch (error) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const chartData = [
    { name: 'Active', value: stats?.active_cranes || 0 },
    { name: 'Idle', value: stats?.idle_cranes || 0 },
    { name: 'Maintenance', value: stats?.maintenance_cranes || 0 },
  ];

  const PIE_COLORS = ['#22c55e', '#ef4444', '#f97316'];

  const handleDebtorsClick = () => {
    navigate('/revenue?tab=debtors');
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">Loading dashboard...</p>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 p-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-foreground">Server is waking up...</p>
          <p className="text-sm text-muted-foreground mt-1">This may take a few seconds. Please wait.</p>
        </div>
        <button 
          onClick={fetchData}
          className="px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors text-sm font-medium"
          data-testid="retry-button"
        >
          Retry Now
        </button>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 lg:p-12 space-y-4 md:space-y-6" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm md:text-base text-muted-foreground mt-1 md:mt-2">Real-time overview of your crane rental operations</p>
        </div>
        <label className="flex flex-col gap-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
          Showing month
          <input type="month" value={ym} max={thisYm} onChange={(e) => e.target.value && setYm(e.target.value)}
            className="bg-card border border-border rounded-md h-10 px-3 text-sm text-foreground normal-case tracking-normal font-normal" data-testid="dashboard-month-picker" />
        </label>
      </div>

      <ExpiringDocsBox />

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <StatCard title="Total Cranes" value={stats?.total_cranes || 0} icon={Truck} onClick={() => navigate('/cranes')} />
        <StatCard title="Active Cranes" value={stats?.active_cranes || 0} icon={Activity} pulse="emerald" onClick={() => navigate('/cranes?status=active')} />
        <StatCard title="Idle Cranes" value={stats?.idle_cranes || 0} icon={Activity} pulse="red" onClick={() => navigate('/cranes?status=idle')} />
        <StatCard title="In Maintenance" value={stats?.maintenance_cranes || 0} icon={Activity} pulse="orange" onClick={() => navigate('/cranes?status=maintenance')} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <StatCard title="Monthly Revenue" value={`₹${(stats?.monthly_revenue || 0).toLocaleString()}`} icon={TrendingUp} onClick={() => navigate('/revenue')}
          trend={<VsPrev cur={stats?.monthly_revenue} prev={stats?.prev_monthly_revenue} higherIsGood label={prevLabel} testId="revenue-vs-prev" />} />
        <StatCard title="Monthly Expenses" value={`₹${(stats?.monthly_expenses || 0).toLocaleString()}`} icon={TrendingDown} onClick={() => navigate('/expenses')}
          trend={<VsPrev cur={stats?.monthly_expenses} prev={stats?.prev_monthly_expenses} label={prevLabel} testId="expenses-vs-prev" />} />
        <StatCard title="Net Profit" value={`₹${(stats?.net_profit || 0).toLocaleString()}`} icon={DollarSign} onClick={() => navigate('/cashflow')}
          trend={<VsPrev cur={stats?.net_profit} prev={stats?.prev_net_profit} higherIsGood label={prevLabel} testId="profit-vs-prev" />} />
        <StatCard title="Debtors" value={`₹${(stats?.total_outstanding || 0).toLocaleString()}`} icon={Users} trend="As of today · Click to view" onClick={handleDebtorsClick} />
      </div>

      {isAdmin && <div className="mt-4 md:mt-6"><CategorySummaryCard ym={ym} /></div>}

      {/* Crane Utilization Chart */}
      <Card className="border border-border rounded-md bg-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleWidget('chart')}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-heading font-bold uppercase tracking-tight text-card-foreground flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              Fleet Status Overview
            </CardTitle>
            {collapsedWidgets['chart'] ? <ChevronDown className="w-5 h-5 text-muted-foreground" /> : <ChevronUp className="w-5 h-5 text-muted-foreground" />}
          </div>
        </CardHeader>
        {!collapsedWidgets['chart'] && (
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={chartData.filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value">
                      {chartData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--card-foreground))', borderRadius: '8px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col justify-center space-y-3">
                {chartData.map((item, i) => (
                  <div key={item.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i] }}></div>
                      <span className="text-sm font-medium text-card-foreground">{item.name}</span>
                    </div>
                    <span className="text-lg font-bold text-card-foreground">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* India Map with Crane Locations */}
      <Card className="border border-border rounded-md bg-card">
        <CardHeader className="pb-2 cursor-pointer" onClick={() => toggleWidget('map')}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="text-lg font-heading font-bold uppercase tracking-tight text-card-foreground flex items-center gap-2">
              <MapPin className="w-5 h-5 text-primary" />
              Crane Locations
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-emerald-500 rounded-full pulse-live"></div>
                <span className="text-muted-foreground">Deployed</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                <span className="text-muted-foreground">Idle</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                <span className="text-muted-foreground">Maintenance</span>
              </div>
              {collapsedWidgets['map'] ? <ChevronDown className="w-5 h-5 text-muted-foreground ml-2" /> : <ChevronUp className="w-5 h-5 text-muted-foreground ml-2" />}
            </div>
          </div>
        </CardHeader>
        {!collapsedWidgets['map'] && (
        <CardContent className="p-0">
          <div className="h-[400px] w-full rounded-b-md overflow-hidden">
            <MapContainer
              center={[22.5937, 78.9629]}
              zoom={5}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {mapMarkers.map((site) => {
                const allIdle = site.cranes.every(c => c.status === 'idle');
                const hasMaintenance = site.cranes.some(c => c.status === 'maintenance');
                const icon = allIdle ? redIcon : hasMaintenance ? orangeIcon : greenIcon;
                
                return (
                  <Marker
                    key={site.siteId}
                    position={site.coordinates}
                    icon={icon}
                  >
                    <Popup maxWidth={280}>
                      <div style={{minWidth: '200px'}}>
                        <p style={{fontWeight:'bold',fontSize:'14px',margin:'0 0 2px 0',color:'#1e293b'}}>{site.siteName}</p>
                        <p style={{fontSize:'12px',color:'#64748b',margin:'0 0 8px 0'}}>{site.location} &bull; {site.cranes.length} crane{site.cranes.length > 1 ? 's' : ''}</p>
                        <div style={{borderTop:'1px solid #e2e8f0',paddingTop:'6px'}}>
                          {site.cranes.map((crane, i) => (
                            <div key={i} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'3px 0',borderBottom: i < site.cranes.length - 1 ? '1px solid #f1f5f9' : 'none'}}>
                              <div>
                                <span style={{fontWeight:600,fontSize:'12px',color:'#334155'}}>{crane.serial_number}</span>
                                <br/>
                                <span style={{fontSize:'11px',color:'#64748b'}}>{crane.equipment} &bull; {crane.capacity}</span>
                              </div>
                              <span style={{
                                fontSize:'10px',
                                padding:'2px 6px',
                                borderRadius:'4px',
                                fontWeight:600,
                                background: crane.status === 'active' ? '#dcfce7' : crane.status === 'maintenance' ? '#ffedd5' : '#fee2e2',
                                color: crane.status === 'active' ? '#166534' : crane.status === 'maintenance' ? '#9a3412' : '#991b1b'
                              }}>
                                {crane.status === 'active' ? 'DEPLOYED' : crane.status.toUpperCase()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
          {mapMarkers.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/80">
              <p className="text-muted-foreground">No crane locations available. Add locations to sites to see them on the map.</p>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        <Card className="col-span-full lg:col-span-8 border border-border rounded-md bg-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-bold uppercase tracking-tight text-card-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/cranes')} data-testid="fleet-status-heading">Fleet Status</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                <YAxis stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', color: 'hsl(var(--card-foreground))', borderRadius: '8px' }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="col-span-full lg:col-span-4 border border-border rounded-md bg-card">
          <CardHeader>
            <CardTitle className="text-lg font-heading font-bold uppercase tracking-tight text-card-foreground cursor-pointer hover:text-primary transition-colors" onClick={() => navigate('/cranes')} data-testid="recent-cranes-heading">Recent Cranes</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cranes.slice(0, 5).map((crane) => (
                <div key={crane.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-md border border-border">
                  <div>
                    <p className="font-mono font-bold text-sm text-card-foreground">{crane.serial_number}</p>
                    <p className="text-xs text-muted-foreground">{crane.equipment}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {crane.status === 'active' && <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-live"></span>}
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${
                      crane.status === 'active' ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                      crane.status === 'maintenance' ? 'bg-orange-500/10 text-orange-600 dark:text-orange-400' :
                      'bg-red-500/10 text-red-600 dark:text-red-400 font-extrabold'
                    }`}>
                      {crane.status === 'active' ? 'Deployed' : crane.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}